/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { iamLogin, iamUtils } from '../main';
import { iamLocalization } from '../localization';
import { Elements } from '../elements/elements';
import { iam } from '../iam';

export abstract class Page {
  public elements: Elements = new Elements();

  public localize(): void {
    // tell the localization engine to execute this callback whenever it's told to perform translation
    iamLocalization.addLocalization(() => {
      iamLogin.localizeElement('footerLegal', {
        stringKey: 'footerLegal',
        localeSubstitution: true,
        localeSeparator: '-',
        stringParameters: {
          year: new Date().getFullYear(),
          startTOSLink: '<a target="_blank" href="https://www.trellix.com/{locale}/about/legal/cloud-terms-of-service.html">',
          startPrivacyLink: '<a target="_blank" href="https://www.trellix.com/{locale}/about/legal/privacy.html">',
          endLink: '</a>'
        },
        rawHTML: true
      });
      const contactUsText = document.getElementById('contactUsText');
      if (contactUsText) {
        iamLogin.localizeElement(contactUsText, 'contactUs');
      }
      const contactUs = document.getElementById('contactUs');
      if (contactUs) {
        iamLogin.localizeElement(contactUs, { string: 'https://www.trellix.com/{locale}/contact-us.html', attribute: 'href', localeSubstitution: true, localeSeparator: '-', rawHTML: true });
      }

      // if there are any field validation error messages showing, re-translate
      document.querySelectorAll<HTMLElement>('.iam-field-error-message').forEach(iamErrorMessage => {
        if (iamErrorMessage.getAttribute('oktaString') === 'true') {
          // this would only be the case when using the widget, and switching languages causes the widget to re-render,
          // so this will likely never happen but handling it just in case: mirror the okta message into our own element.
          const oktaMessage = iamErrorMessage.parentElement?.querySelector<HTMLElement>('iam-okta-field-error-message');
          if (oktaMessage) {
            iamErrorMessage.textContent = oktaMessage.textContent;
          }
        } else {
          iamLogin.localizeElement(iamErrorMessage, 'requiredField');
        }
      });
      // if there are any registered localization callbacks, call them
      iamLogin.localizationCallbacks.forEach(registration => registration.callback());
    });
  }

  public abstract initialize(): void;

  public validateForm() {
  }

  public localeChanged(locale: string) {
  }

  /**
   * Show or hide common fields
   * @param fields array of fields to show (others will be hidden)
   */
  public showCommonFields(fields: string[]) {
    document.getElementById('sub-head-link').style.display = fields.indexOf('sub-head-link') >= 0 ? '' : 'none';
    document.getElementById('sub-header-2').style.display = fields.indexOf('sub-header-2') >= 0 ? '' : 'none';
    document.getElementById('explanation').style.display = fields.indexOf('explanation') >= 0 ? '' : 'none';
    document.getElementById('done-message').style.display = fields.indexOf('done-message') >= 0 ? '' : 'none';
  }

  public addValidationError(target: HTMLElement, optionalOktaMessage?: string) {
    target = this.elements.getFieldsetFromDescendent(target, '.o-form-input');
    const field = this.elements.getFieldsetFromDescendent(target, '.okta-form-input-field.o-form-control');
    field.classList.add('iam-field-error');
    let error = this.elements.getFieldsetFromDescendent(target, '.iam-field-error-message');
    // are we replacing an existing validation error or creating it from scratch?
    if (!error) {
      error = document.createElement('p');
      error.className = 'iam-field-error-message';
      target.appendChild(error);
    }
    error.textContent = optionalOktaMessage || iamLocalization.localize('requiredField');
    error.setAttribute('oktaString', optionalOktaMessage ? 'true' : 'false');
    if (iam.settings.debug) console.log('adding validation error', target, error);
  }

  public removeValidationError(target: HTMLElement) {
    target = this.elements.getFieldsetFromDescendent(target, '.o-form-input');
    const field = this.elements.getFieldsetFromDescendent(target, '.okta-form-input-field.o-form-control');
    if (field.classList.contains('iam-field-error')) {
      field.classList.remove('iam-field-error');
      const ourElement = target.querySelector('.iam-field-error-message');
      if (iam.settings.debug) console.log('removing validation error', target, ourElement);
      ourElement.parentElement.removeChild(ourElement);
    } // else no error is present
  }

  /**
   * Initialization that's common to all widget and pseudo-widget states
   */
  public initializeCommon() {
    const container = this.elements.getFieldsetContainer();
    if (container) {
      // add validation checking to all input fields
      container.querySelectorAll('input[type="text"], input[type="tel"], input[type="password"]').forEach(one => { one.addEventListener('input', () => this.validateForm()); });
      // set up mutation observer to watch for input elements added in the future, for validation checking.
      (new MutationObserver((mutationList, observer) => {
        for (let i = 0; i < mutationList.length; i++) {
          if (mutationList[i].target instanceof HTMLElement) {
            const target = mutationList[i].target as HTMLElement;
            for (let j = 0; j < mutationList[i].addedNodes.length; j++) {
              if (mutationList[i].addedNodes[j] instanceof Element) {
                const addedNode = mutationList[i].addedNodes[j] as Element;
                if (addedNode.tagName === 'INPUT') {
                  switch (addedNode.getAttribute('type')) {
                    case 'text':
                    case 'tel':
                    case 'password':
                      addedNode.addEventListener('input', () => this.validateForm());
                      break;
                  }
                } else if (addedNode.classList.contains('okta-form-input-error')) {
                  // okta's trying to show a validation error for this field, but we show a different message and do different styling
                  if (iam.settings.debug) console.log('field validation error from okta: ' + addedNode.textContent, addedNode);
                  addedNode.classList.add('iam-okta-field-error-message'); // hides okta's message html
                  this.addValidationError(target, addedNode.textContent);
                }
              }
            }
            for (let j = 0; j < mutationList[i].removedNodes.length; j++) {
              if (mutationList[i].removedNodes[j] instanceof Element) {
                const removedNode = mutationList[i].removedNodes[j] as Element;
                if (removedNode.classList.contains('okta-form-input-error')) {
                  // okta just removed their validation error for this field, so we should remove ours
                  if (iam.settings.debug) console.log('field validation error cleared by okta', removedNode);
                  this.removeValidationError(target);
                }
              }
            }
          }
        }
      })).observe(container, { childList: true, subtree: true });
      // do a validation check right off the bat
      this.validateForm();
    }
    // when the form is submitted, hide the error message (e.g. "bad password" should go away when the user enters their password again and submits)
    if (document.forms.length) {
      // apply to the last form because when the widget transitions between contexts, the new one will be added before the old one is removed
      document.forms[document.forms.length - 1].addEventListener('submit', function () {
        iamLogin.hideMessageBanners();
      });
    }
    iamLogin.pageInitialized = true;
    // trigger translation of all localizable strings now that the dom has stabilized
    iamLocalization.translate();
  }
}
